import { ColDef, ColGroupDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { createCalendarCellClickHandler, CreateCellClickHandler, CustomCellClickAction, InsertRowHandler, RemoveRowHandler, postSortHandler, useCellChangeHandler, GridCellConfig, createCellCssClassRules } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { defaultEQM2050_R01Dto, EQM2050_R01Dto } from "./Dto_EQM2050_R01";
import { useEQM2050_R01Store, useEQM2050_R01Store_GridCalendar } from "./Store_EQM2050_R01";
import { createBaseColumnDefs, createColumnDefs, headerNames } from "./components/ColumnDefinitions";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { formatDate } from "src/utils/string/stringUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'EQM2050_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<EQM2050_R01Dto> | null>;
}

export const Table_EQM2050_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef } = props;

	const {
		EQM2050_R01List,
		setEQM2050_R01List,
		updateEQM2050_R01List,
		insertEQM2050_R01List,
		removeEQM2050_R01List,
		selectedEQM2050_R01List,
		setSelectedEQM2050_R01List,
		initSelectedEQM2050_R01List,
		allSelectedEQM2050_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		originalEQM2050_R01List,
		deletedEQM2050_R01List,
		addDeletedEQM2050_R01List,
	} = useEQM2050_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	// 달력 팝업 노출 여부 상태
	const { EQM2050_R01GridCalendarState, setEQM2050_R01GridCalendarState } = useEQM2050_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = EQM2050_R01GridCalendarState;

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: EQM2050_R01List,
		defaultGridData: defaultEQM2050_R01Dto,
		selectedRow: selectedEQM2050_R01List.map(id => String(id)), // number[] → string[] 변환 (공통 모듈 호환)
		originalGridData: originalEQM2050_R01List,
		updateRow: (index: number, data: EQM2050_R01Dto) => updateEQM2050_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['inspectionDt'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['industrialWater', 'domesticWater', 'electricalEnergy01', 'electricalEnergy02', 'electricalEnergy03', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: [''],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<EQM2050_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedEQM2050_R01List,
			deletedList: deletedEQM2050_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		createCellClassRules,
		selectedEQM2050_R01List, deletedEQM2050_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	// 셀 값 변경 핸들러
	const handleCellValueChanged = useCellChangeHandler<EQM2050_R01Dto>({
		dataList: EQM2050_R01List,
		originalDataList: originalEQM2050_R01List,

		updateRow: (valueIndex: string | number, updatedData: EQM2050_R01Dto) => {
			updateEQM2050_R01List(Number(valueIndex), updatedData);
		},

	});
	
	// 편집 가능한 컬럼 목록
	const defaultEditableColumns = ['inspectionDt', 'industrialWater', 'domesticWater', 'electricalEnergy01', 'electricalEnergy02', 'electricalEnergy03', 'remark'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: EQM2050_R01List,
			rowData: EQM2050_R01List[rowIndex],
			insertRow: insertEQM2050_R01List,
			defaultRowData: defaultEQM2050_R01Dto,
			setClickable
		});
	}, [EQM2050_R01List, insertEQM2050_R01List, defaultEQM2050_R01Dto, setClickable]);

	// 순번 컬럼 렌더러 (표준 renderIndexCell 사용)
	const indexCellRenderer = useMemo(() => {
		return (params: any) => {
			return renderIndexCell(
				params,
				(innerParams: EQM2050_R01Dto) => {
					// valueIndex 기준으로 삭제/선택 표시 확인 (숫자로 비교)
					const isDeleted = !!(innerParams.valueIndex && deletedEQM2050_R01List.includes(innerParams.valueIndex));
					const isSelected = !!(innerParams.valueIndex && selectedEQM2050_R01List.includes(innerParams.valueIndex));
					const isInserted = EQM2050_R01List.find(value => value.valueIndex === innerParams.valueIndex)?.inserted === true;
					const isModified = EQM2050_R01List.find(value => value.valueIndex === innerParams.valueIndex)?.isModified === true && !isInserted;
					const isTransparent = isDeleted || isSelected || isInserted || isModified;
					return { isDeleted, isSelected, isInserted, isModified, isTransparent };
				},
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData: EQM2050_R01Dto) => {
					RemoveRowHandler(
						gridRef,
						EQM2050_R01List,
						rowData,
						setClickable,
						(valueIndex: number) => removeEQM2050_R01List(valueIndex),
						(ids: string[]) => addDeletedEQM2050_R01List(ids.map(id => Number(id))) // string[] → number[] 변환
					);
				}
			);
		};
	}, [renderIndexCell, deletedEQM2050_R01List, selectedEQM2050_R01List, EQM2050_R01List, insertEQM2050_R01List, removeEQM2050_R01List, addDeletedEQM2050_R01List, setClickable, gridRef]);

	const baseColumnDefs: (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[] = useMemo(
		() => createBaseColumnDefs(cellDateComparator),
		[indexCellRenderer, hoveredRowIndex, EQM2050_R01List, selectedEQM2050_R01List, deletedEQM2050_R01List]
	);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: (ColDef<EQM2050_R01Dto> | ColGroupDef<EQM2050_R01Dto>)[] = useMemo(
		() => createColumnDefs(baseColumnDefs, columnState),
		[baseColumnDefs, columnState]
	);

	const handleRowClick = useCallback((event: RowClickedEvent<EQM2050_R01Dto>) => {
		if (event.node.rowPinned === 'bottom' || !event.data) return;
		if (!clickable) {
			setClickable(true);
			return;
		}

		const valueIndex = Number(event.data.valueIndex);
		if (deletedEQM2050_R01List.includes(valueIndex)) return;

		const isSelected = selectedEQM2050_R01List.includes(valueIndex);
		setSelectedEQM2050_R01List(isSelected ? [] : [valueIndex]);
	}, [clickable, deletedEQM2050_R01List, selectedEQM2050_R01List, setClickable, setSelectedEQM2050_R01List]);

	// 범용 이벤트 훅 사용 - 방향키 이동 기능
	const cellEventHandlers = useDetailTableEvents({
		detailList: EQM2050_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	// 셀 클릭 액션 설정
	const cellClickActions: CustomCellClickAction<EQM2050_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'inspectionDt',
			action: createCalendarCellClickHandler('inspectionDt', EQM2050_R01GridCalendarState, setEQM2050_R01GridCalendarState),
		},
		{
			validate: (field, data) => defaultEditableColumns.includes(field),
			// aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [EQM2050_R01GridCalendarState, setEQM2050_R01GridCalendarState]);
	const handleCellClick = CreateCellClickHandler(EQM2050_R01List, 'valueIndex', cellClickActions);

	// 달력 변경 핸들러
	const handleCalendarChange = useCallback((date: Date | undefined, rowIndex: number, targetField: string) => {
		if (rowIndex !== null && targetField) {
			const foundData = EQM2050_R01List[rowIndex];
			if (foundData) {
				updateEQM2050_R01List(foundData.valueIndex, {
					...foundData,
					[targetField]: date ? formatDate(date) : '',
					isModified: true // 날짜 변경 시 수정 상태로 설정
				});
			}
		}
	}, [EQM2050_R01List, updateEQM2050_R01List]);

	// 달력 로드 핸들러
	const handleCalendarLoad = useCallback((rowIndex: number, targetField: string) => {
		const foundData = EQM2050_R01List[rowIndex];
		if (!foundData) return;

		const dateString = foundData[targetField as keyof EQM2050_R01Dto] as string;
		
		if (dateString && dateString !== '') {
			try {
				const date = new Date(dateString);
				if (!isNaN(date.getTime())) {
					setEQM2050_R01GridCalendarState({ targetDate: date });
				} else {
					setEQM2050_R01GridCalendarState({ targetDate: new Date() });
				}
			} catch {
				setEQM2050_R01GridCalendarState({ targetDate: new Date() });
			}
		} else {
			setEQM2050_R01GridCalendarState({ targetDate: new Date() });
		}
	}, [EQM2050_R01List, setEQM2050_R01GridCalendarState]);

	// 정렬 핸들러
	const handlePostSort = useCallback((params: PostSortRowsParams<EQM2050_R01Dto>) => {
		postSortHandler(params, EQM2050_R01List, originalEQM2050_R01List, setEQM2050_R01List);
	}, [EQM2050_R01List, originalEQM2050_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		inspectionDt: EQM2050_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [EQM2050_R01List]);

	return (
		<GridContextWrapper<EQM2050_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			setPaddedGridData={setEQM2050_R01List}
			initSelected={initSelectedEQM2050_R01List}
			defaultRowData={defaultEQM2050_R01Dto}
			allSelected={allSelectedEQM2050_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				
				rowClassRules={createRowClassRules<EQM2050_R01Dto>({
					idKey: 'valueIndex', // valueIndex 기준으로 선택/삭제 표시 (다른 페이지와 동일한 방식)
					selectedList: selectedEQM2050_R01List,
					deletedList: deletedEQM2050_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellValueChanged}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setEQM2050_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(pos) => setEQM2050_R01GridCalendarState({ pickerPosition: pos })}
				onClear={() => {
					if (targetRowIndex !== null && targetField) {
						handleCalendarChange(undefined, targetRowIndex, targetField);
					}
				}}
				onChange={handleCalendarChange}
				onClickLoad={handleCalendarLoad}
			/>
		</GridContextWrapper>
	);
};

